/**
 * Documentation/About Page
 * Comprehensive information about the project
 */

import React, { useState, useEffect } from 'react';
import '../styles/Documentation.css';
import { API_CONFIG } from '../config/api';

const Documentation = ({ onClose }) => {
  const [projectInfo, setProjectInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectInfo();
  }, []);

  const fetchProjectInfo = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECT_INFO);
      const data = await response.json();
      setProjectInfo(data.project);
    } catch (error) {
      console.error('Error fetching project info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="documentation-modal">
        <div className="documentation-overlay" onClick={onClose}></div>
        <div className="documentation-panel">
          <p>Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (!projectInfo) {
    return (
      <div className="documentation-modal">
        <div className="documentation-overlay" onClick={onClose}></div>
        <div className="documentation-panel">
          <p>Error loading documentation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documentation-modal">
      <div className="documentation-overlay" onClick={onClose}></div>
      <div className="documentation-panel">
        <div className="doc-header">
          <div>
            <h1>{projectInfo.title}</h1>
            <p className="doc-subtitle">{projectInfo.subtitle}</p>
          </div>
          <button className="doc-close" onClick={onClose}>✕</button>
        </div>

        <div className="doc-tabs">
          {['overview', 'technical', 'usecases', 'limitations'].map((tab) => (
            <button
              key={tab}
              className={`doc-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="doc-content">
          {activeTab === 'overview' && (
            <div className="doc-section">
              <h2>Overview</h2>
              <p>{projectInfo.description}</p>

              <h3>Covered Sectors</h3>
              <div className="sectors-grid">
                {projectInfo.sectors.map((sector, idx) => (
                  <div key={idx} className="sector-badge">{sector}</div>
                ))}
              </div>

              <h3>Key Features</h3>
              <ul>
                {projectInfo.key_features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'technical' && (
            <div className="doc-section">
              <h2>Technical Architecture</h2>

              <h3>System Architecture</h3>
              <p><strong>Type:</strong> {projectInfo.technical_overview.architecture}</p>

              <h3>Machine Learning</h3>
              <p><strong>Available Backends:</strong></p>
              <ul>
                {projectInfo.technical_overview.ml_backends.map((backend, idx) => (
                  <li key={idx}>{backend}</li>
                ))}
              </ul>

              <p><strong>Prediction Approach:</strong> {projectInfo.technical_overview.prediction_approach}</p>
              <p><strong>Data Source:</strong> {projectInfo.technical_overview.data_source}</p>

              <h3>Implementation Details</h3>
              <div className="tech-details">
                <p>
                  The system uses <strong>artificial neural networks</strong> with multiple layers to learn complex patterns in governance data.
                  Each model is trained with:
                </p>
                <ul>
                  <li><strong>Hidden Layers:</strong> 3 layers (128 → 64 → 32 neurons)</li>
                  <li><strong>Activation Function:</strong> ReLU with dropout regularization</li>
                  <li><strong>Optimization:</strong> Adam optimizer with early stopping</li>
                  <li><strong>Training Data:</strong> Synthetic Nigerian demographic data</li>
                  <li><strong>Validation Split:</strong> 20% held-out validation set</li>
                  <li><strong>Early Stopping:</strong> Patience of 15 epochs to prevent overfitting</li>
                </ul>
              </div>

              <h3>API Documentation</h3>
              <p>Full API documentation available at: <code>{API_CONFIG.BASE_URL}/api/v1/docs</code></p>
            </div>
          )}

          {activeTab === 'usecases' && (
            <div className="doc-section">
              <h2>Use Cases & Applications</h2>
              {projectInfo.use_cases.map((useCase, idx) => (
                <div key={idx} className="usecase-card">
                  <h3>{useCase[0]}</h3>
                  <p>{useCase[1]}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'limitations' && (
            <div className="doc-section">
              <h2>Important Limitations</h2>
              <div className="limitations-box">
                <ul>
                  {projectInfo.limitations.map((limitation, idx) => (
                    <li key={idx}>{limitation}</li>
                  ))}
                </ul>
              </div>
              <p className="disclaimer">
                <strong>Disclaimer:</strong> These predictions are AI-generated and should inform decision-making,
                not replace expert judgment and human oversight. Always validate results with domain experts.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documentation;
